import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import moment from "moment";
import { Box } from "@mui/material";
import {
  MyLeaveAllLeavesWithCoverage,
  MyLeavesAllLeavesCard,
} from "my-leave/components";
import { useMyLeaveEvents, useMyLeaveUsersWithRequest } from "my-leave/hooks";
import { OcInfoDialog, OcPageNameTitle, OcPageTransition } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcTableState,
  useOcClientState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcLocalState,
  useOcRoleFilters,
} from "oc/hooks";

export const MyLeaveViewLeaveRequestsPage = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();

  let { contractCode: contract } = useOcCurrentUserState();

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract,
    product: "MyLeave",
    role: "viewer",
  });

  const actualDate = new Date();
  const [currentDate, setCurrentDate] = useState(actualDate);
  const [prevCurrentDate, setPrevCurrentDate] = useState(actualDate);

  const [showDialog, setShowDialog] = useState(false);

  //By request, useLocalState is used here. (OW-2024-10-301)
  const [onlyRequested, setOnlyRequested] = useOcLocalState(
    "showOnlyRequestedLeaves",
    false,
  );

  const [prevOnlyRequested, setPrevOnlyRequested] = useOcClientState(
    "prevShowOnlyRequestedLeaves",
    true,
  );

  const [filterable, setFilterable] = useOcClientState<boolean>(
    "allLeavesFilter",
    false,
  );

  let tableId = "allLeavesViewerTableData";

  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });
  const [prevTableData, setPrevTableData] = useOcTableState(
    `${tableId}Prev`,
    {
      ...initialTableData,
      sortField: "name",
      pageSize: 5,
    },
  );

  const [showRemoteWorks, setShowRemoteWorks] = useOcClientState<boolean>(
    "showRemoteWorks",
    true,
  );

  const [prevShowRemoteWorks, setPrevShowRemoteWorks] =
    useOcClientState<boolean>("prevShowRemoteWorks", true);

  let startDate = moment(currentDate).startOf("month").toDate();
  let endDate = moment(currentDate).endOf("month").toDate();

  const handleCompleted = () => {
    handleChangeFilterable(false);
  };

  const {
    loadQuery: loadUsers,
    data,
    loading,
  } = useMyLeaveUsersWithRequest({
    onlyRequested,
    roleFilters,
    tableData,
    showRemoteWorks,
    role: "viewer",
    startDate,
    endDate,
    onCompleted: handleCompleted,
  });

  const {
    loadEvents,
    data: leavesData,
    loading: leaveEventsLoading,
    eventsByDays,
  } = useMyLeaveEvents({
    roleFilters,
    currentDate: moment(currentDate).toDate(),
    viewedByAdmin: true,
    contract: contract,
    role: "viewer",
  });

  const handleChangeFilterable = (state: boolean) => {
    setFilterable(state);
  };

  const [filterItemErrors, setFilterItemErrors] = useState<string[]>([]);

  const MAX_ITEM_COUNT = 1000;
  const generateRoleFilterError = () => {
    const newState = [...filterItemErrors];
    if (roleFilters?.company && roleFilters.company.length > MAX_ITEM_COUNT) {
      newState.push(t("OC_COMPANY"));
      setFilterItemErrors(newState);
    }
    if (roleFilters?.orgUnit && roleFilters.orgUnit.length > MAX_ITEM_COUNT) {
      newState.push(t("OC_ORGUNIT"));
      setFilterItemErrors(newState);
    }

    if (
      roleFilters?.workingPlace &&
      roleFilters.workingPlace.length > MAX_ITEM_COUNT
    ) {
      newState.push(t("WORKINGPLACE"));
      setFilterItemErrors(newState);
    }
    if ((roleFilters?.jobClassGroup?.length ?? 0) > MAX_ITEM_COUNT) {
      newState.push(t("OC_JOBCLASSGROUP"));
      setFilterItemErrors(newState);
    }
    if ((roleFilters?.payOffice?.length ?? 0) > MAX_ITEM_COUNT) {
      newState.push(t("OC_PAYOFFICE"));
      setFilterItemErrors(newState);
    }
    if ((roleFilters?.contract?.length ?? 0) > MAX_ITEM_COUNT) {
      newState.push(t("OC_GET_EMPLOYEE"));
      setFilterItemErrors(newState);
    }

    return newState?.length > 0 ? newState : false;
  };

  const handleLoadQuery = () => {
    const isItemError = generateRoleFilterError();
    if (isItemError) {
      setShowDialog(true);
      handleChangeFilterable(false);
      return;
    }
    loadUsers();
    loadEvents();
    setPrevRoleFilters(roleFilters);
    setPrevShowRemoteWorks(showRemoteWorks);
    setPrevOnlyRequested(onlyRequested);
  };

  const buttonDisable =
    _.isEqual(roleFilters, prevRoleFilters) &&
    showRemoteWorks === prevShowRemoteWorks &&
    onlyRequested === prevOnlyRequested;

  const handleOnlyReqestedChange = (state: boolean) => {
    setPrevOnlyRequested(onlyRequested);
    setOnlyRequested(state);
  };

  const handleCurrentDateChange = (date: Date | string) => {
    setPrevCurrentDate(currentDate);
    setCurrentDate(typeof date === "string" ? new Date(date) : date);
  };

  if (currentDate !== prevCurrentDate) {
    loadUsers();
    loadEvents();
    setPrevCurrentDate(currentDate);
  }

  const handleTableDataChange = (state: any) => {
    const keys = Object.keys(state).toString();
    setTableData(state);
    if (
      keys === "searchField" ||
      (keys === "searchText" && state?.searchText?.length < 3)
    ) {
      return;
    }
    loadUsers();
    loadEvents();
  };

  const handleShowRemoteWorksChange = (newValue: boolean) => {
    setShowRemoteWorks(newValue);
  };

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  const originalonSelectRow = (o: any) => {
    if (isMobile) {
      history.push(`/my-leave/user-requests/MyLeave/viewer/${o?.row?.contract}`);
    } else {
      history.push(`/my-leave/user-requests/MyLeave/viewer/${o.contract}`);
    }
  };

  const onSelectRow = (o: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? undefined : o?._id);
    } else {
      originalonSelectRow(o);
    }
  };

  const handleClearFilters = () => {
    resetRoleFilters();
    handleChangeFilterable(false);
    setFilterItemErrors([]);
  };

  const isTabledataChanged = !_.isEqual(tableData, prevTableData);
  useEffect(() => {
    if (isTabledataChanged) {
      handleLoadQuery();
      setPrevTableData(tableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabledataChanged]);

  const handleDialogClose = () => {
    setShowDialog(false);
    setFilterItemErrors([]);
  };

  useEffect(() => {
    loadUsers();
    loadEvents();
    setPrevRoleFilters(roleFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  return (
    <OcPageTransition>
      <Box>
        <OcInfoDialog
          open={showDialog}
          onClose={handleDialogClose}
          text={t("OC_TOO_MANY_HITS")}
          handleDialogClose={handleDialogClose}
          isMobile={isMobile}
          filterItemErrors={filterItemErrors}
        />
        <OcPageNameTitle title={t("OC_REQUESTS_VIEW")} goBack={false} />
        <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
          <MyLeavesAllLeavesCard
            label={t("MY_LEAVE_LEAVE_AND_ABSENCE_REQUESTS")}
            buttonDisable={buttonDisable}
            currentDate={currentDate}
            data={data}
            filterable={filterable}
            loading={loading}
            onAddFilter={handleAddFilter}
            onClearFilters={handleClearFilters}
            onCurrentDateChange={handleCurrentDateChange}
            onFilterableChange={handleChangeFilterable}
            onLoadQuery={handleLoadQuery}
            onlyRequested={onlyRequested}
            onOnlyRequestedChange={handleOnlyReqestedChange}
            onRemoveFilter={handleRemoveFilter}
            onSelectRow={onSelectRow}
            onRowSelection={onSelectRow}
            onShowRemoteWorksChange={handleShowRemoteWorksChange}
            onTableDataChange={handleTableDataChange}
            product="MyLeave"
            role="viewer"
            roleFilters={roleFilters}
            showRemoteWorks={showRemoteWorks}
            tableData={tableData}
            tableId={tableId}
            contract={contract}
            examinerDasboard={false}
            selectedRowId={selectedRowId}
            handleMoreButton={originalonSelectRow}
          />
          <Box height="16px" />
          <MyLeaveAllLeavesWithCoverage
            cardTitle={t("MY_LEAVE_LEAVES_AND_REMOTE_WORKS")}
            currentDate={currentDate}
            onCurrentDateChange={handleCurrentDateChange}
            monthFiltering={true}
            showRemoteWorks={showRemoteWorks}
            viewedByAdmin={true}
            leavesData={leavesData}
            eventsByDays={eventsByDays}
            loading={leaveEventsLoading}
          />
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyLeaveViewLeaveRequestsPage;
